import { useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  BookOpen,
  Calculator,
  FileText,
  Pencil,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  PageHeaderSkeleton,
  StatCardsSkeleton,
} from "~/components/ui/skeletons";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import {
  useCompany,
  useUpdateCompany,
} from "~/hooks/contabilidad/useCompanies";
import { useJournalsByCompany } from "~/hooks/contabilidad/useJournals";
import { useMovements } from "~/hooks/contabilidad/useMovements";
import type { CompanyFormData } from "~/types/contabilidad";

import { EmpresaDialog } from "./components/EmpresaDialog";

export default function EmpresaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: company, isLoading } = useCompany(id!);
  const { data: journals = [] } = useJournalsByCompany(id!);
  const { data: accounts = [] } = useAccounts({ company_id: id });
  const { data: movements = [] } = useMovements({ company_id: id });
  const updateMutation = useUpdateCompany();

  const handleUpdate = (data: CompanyFormData) => {
    updateMutation.mutate(
      { id: id!, data },
      {
        onSuccess: () => {
          setEditDialogOpen(false);
        },
      }
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <StatCardsSkeleton count={3} className="lg:grid-cols-3" />
      </PageContainer>
    );
  }

  if (!company) {
    return (
      <PageContainer>
        <p className="text-muted-foreground">Empresa no encontrada.</p>
        <Button
          variant="link"
          onClick={() => navigate("/contabilidad/empresas")}
          className="mt-4"
        >
          Volver a la lista
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={company.name}
        description={`Creada el ${dayjs(company.created_at).format("DD/MM/YYYY")}`}
        back={
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-9 text-muted-foreground"
            onClick={() => navigate("/contabilidad/empresas")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
        }
        actions={
          <Button onClick={() => setEditDialogOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" />
            Editar
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Diarios</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{journals.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cuentas</CardTitle>
            <Calculator className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{accounts.length}</div>
          </CardContent>
        </Card>
        <Card className="col-span-2 lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Movimientos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{movements.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="info" className="space-y-4">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="info">Información</TabsTrigger>
          <TabsTrigger value="diarios">Diarios</TabsTrigger>
          <TabsTrigger value="cuentas">Cuentas</TabsTrigger>
          <TabsTrigger value="movimientos">Movimientos</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <Card>
            <CardHeader>
              <CardTitle>Información General</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Nombre</p>
                  <p className="font-medium">{company.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Moneda</p>
                  <p className="font-medium">{company.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">País</p>
                  <p className="font-medium">{company.country}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Fecha de Creación
                  </p>
                  <p className="font-medium">
                    {dayjs(company.created_at).format("DD/MM/YYYY HH:mm")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="diarios">
          <Card>
            <CardHeader>
              <CardTitle>Diarios de la Empresa</CardTitle>
            </CardHeader>
            <CardContent>
              {journals.length === 0 ? (
                <p className="text-muted-foreground">
                  No hay diarios registrados.
                </p>
              ) : (
                <div className="space-y-2">
                  {journals.map((journal) => (
                    <div
                      key={journal.id}
                      className="flex cursor-pointer items-center justify-between rounded-lg border p-3 hover:bg-muted/50"
                      onClick={() =>
                        navigate(`/contabilidad/diarios/${journal.id}`)
                      }
                    >
                      <div>
                        <p className="font-medium">{journal.name}</p>
                        <p className="text-sm text-muted-foreground">
                          Código: {journal.code}
                        </p>
                      </div>
                      <Badge variant="outline">{journal.type}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cuentas">
          <Card>
            <CardHeader>
              <CardTitle>Cuentas de la Empresa</CardTitle>
            </CardHeader>
            <CardContent>
              {accounts.length === 0 ? (
                <p className="text-muted-foreground">
                  No hay cuentas registradas.
                </p>
              ) : (
                <div className="space-y-2">
                  {accounts.map((account) => (
                    <div
                      key={account.id}
                      className="flex cursor-pointer items-center justify-between rounded-lg border p-3 hover:bg-muted/50"
                      onClick={() =>
                        navigate(`/contabilidad/cuentas/${account.id}`)
                      }
                    >
                      <div>
                        <p className="font-medium">
                          {account.code} - {account.name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {account.account_type}
                        </p>
                      </div>
                      <Badge
                        variant={account.is_active ? "default" : "secondary"}
                      >
                        {account.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movimientos">
          <Card>
            <CardHeader>
              <CardTitle>Movimientos Recientes</CardTitle>
            </CardHeader>
            <CardContent>
              {movements.length === 0 ? (
                <p className="text-muted-foreground">
                  No hay movimientos registrados.
                </p>
              ) : (
                <div className="space-y-2">
                  {movements.slice(0, 10).map((movement) => (
                    <div
                      key={movement.id}
                      className="flex cursor-pointer items-center justify-between rounded-lg border p-3 hover:bg-muted/50"
                      onClick={() =>
                        navigate(`/contabilidad/movimientos/${movement.id}`)
                      }
                    >
                      <div>
                        <p className="font-medium">{movement.ref}</p>
                        <p className="text-sm text-muted-foreground">
                          {dayjs(movement.move_date).format("DD/MM/YYYY")}
                        </p>
                      </div>
                      <Badge
                        variant={
                          movement.is_published ? "default" : "secondary"
                        }
                      >
                        {movement.is_published ? "Publicado" : "Borrador"}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EmpresaDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        company={company}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
      />
    </PageContainer>
  );
}
